/**
 * Ethiopian Calendar → Gregorian conversion utilities
 */

// ───────────────────────────────────────────────────────────────
// Constants
// ───────────────────────────────────────────────────────────────

const JD_EPOCH_ETHIOPIAN = 1723856;

const ETHIOPIAN_MONTHS = [
  "Meskerem",
  "Tikmt",
  "Hidar",
  "Tahsas",
  "Tir",
  "Yekatit",
  "Megabit",
  "Miazia",
  "Ginbot",
  "Sene",
  "Hamle",
  "Nehase",
  "Pagume",
];

const ETHIOPIAN_MONTHS_SHORT = [
  "Mes",
  "Tik",
  "Hid",
  "Tah",
  "Tir",
  "Yek",
  "Meg",
  "Mia",
  "Gin",
  "Sen",
  "Ham",
  "Neh",
  "Pag",
];

// ───────────────────────────────────────────────────────────────
// Ethiopian → Gregorian
// ───────────────────────────────────────────────────────────────

export const ethToGregorian = (eyear, emonth, eday) => {
  const jdn =
    JD_EPOCH_ETHIOPIAN +
    365 * eyear +
    Math.floor(eyear / 4) +
    30 * (emonth - 1) +
    eday -
    1;

  const l = jdn + 68569;
  const n = Math.floor((4 * l) / 146097);

  const l2 = l - Math.floor((146097 * n + 3) / 4);

  const i = Math.floor((4000 * (l2 + 1)) / 1461001);

  const l3 = l2 - Math.floor((1461 * i) / 4) + 31;

  const j = Math.floor((80 * l3) / 2447);

  const day = l3 - Math.floor((2447 * j) / 80);

  const l4 = Math.floor(j / 11);

  const month = j + 2 - 12 * l4;

  const year = 100 * (n - 49) + i + l4;

  return new Date(year, month - 1, day + 1);
};

// ───────────────────────────────────────────────────────────────
// Gregorian → Ethiopian
// ───────────────────────────────────────────────────────────────

export const gregorianToEth = (date) => {
  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();

  const a = Math.floor((14 - m) / 12);
  const yy = y + 4800 - a;
  const mm = m + 12 * a - 3;

  const jdn =
    d +
    Math.floor((153 * mm + 2) / 5) +
    365 * yy +
    Math.floor(yy / 4) -
    Math.floor(yy / 100) +
    Math.floor(yy / 400) -
    32045;

  const r = (jdn - JD_EPOCH_ETHIOPIAN) % 1461;

  const n = (r % 365) + 365 * Math.floor(r / 1460);

  const eyear =
    Math.floor((jdn - JD_EPOCH_ETHIOPIAN) / 1461) * 4 +
    Math.floor(r / 365) -
    Math.floor(r / 1460);

  const emonth = Math.floor(n / 30) + 1;

  const eday = (n % 30) + 1;

  return {
    year: eyear,
    month: emonth,
    day: eday,
    monthName: ETHIOPIAN_MONTHS[emonth - 1],
  };
};

// ───────────────────────────────────────────────────────────────
// Ethiopian Billing Month
// ───────────────────────────────────────────────────────────────

export const getEthiopianBillingMonth = (
  referenceDate = new Date()
) => {
  const eth = gregorianToEth(referenceDate);

  let { year, month, day } = eth;

  // Pagume belongs to Nehase billing cycle
  if (month === 13) {
    month = 12;
    day = 21;
  }

  let startEthYear = year;
  let startEthMonth = month;

  if (day < 21) {
    startEthMonth -= 1;

    if (startEthMonth < 1) {
      startEthMonth = 12;
      startEthYear -= 1;
    }
  }

  let endEthYear = startEthYear;
  let endEthMonth = startEthMonth + 1;

  if (endEthMonth > 12) {
    endEthMonth = 1;
    endEthYear += 1;
  }

  const startDate = ethToGregorian(
    startEthYear,
    startEthMonth,
    21
  );

  const nextPeriodStart = ethToGregorian(
    endEthYear,
    endEthMonth,
    21
  );

  const endDate = new Date(nextPeriodStart);

  endDate.setDate(endDate.getDate() - 1);

  const startMonthName =
    ETHIOPIAN_MONTHS[startEthMonth - 1];

  const endMonthName =
    ETHIOPIAN_MONTHS[endEthMonth - 1];

  const label =
    startEthMonth === 12
      ? `${startMonthName} 21, ${startEthYear} – ${endMonthName} 20, ${endEthYear} (incl. Pagume)`
      : `${startMonthName} 21, ${startEthYear} – ${endMonthName} 20, ${endEthYear}`;

  return {
    startDate,
    endDate,
    label,
  };
};

// ───────────────────────────────────────────────────────────────
// Format Ethiopian Date
// ───────────────────────────────────────────────────────────────

export const formatEthiopianDate = (
  date,
  options = {}
) => {
  if (!date) return "—";

  const d = new Date(date);

  if (isNaN(d)) return "—";

  const eth = gregorianToEth(d);

  const monthName = options.shortMonth
    ? ETHIOPIAN_MONTHS_SHORT[eth.month - 1]
    : ETHIOPIAN_MONTHS[eth.month - 1];

  let result = `${monthName} ${eth.day}, ${eth.year}`;

  if (options.includeTime) {
    const hours = d.getHours();

    const minutes = String(
      d.getMinutes()
    ).padStart(2, "0");

    const ampm = hours >= 12 ? "PM" : "AM";

    const h12 = hours % 12 || 12;

    result += ` ${h12}:${minutes} ${ampm}`;
  }

  return result;
};

// ───────────────────────────────────────────────────────────────
// Dual Calendar Formatter
// ───────────────────────────────────────────────────────────────

export const formatDualDate = (date, options = {}) => {
  if (!date) return "—";

  const d = new Date(date);

  if (isNaN(d)) return "—";

  const eth = formatEthiopianDate(d);

  const gc = d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });

  let time = "";

  if (options.includeTime) {
    const hours = d.getHours();

    const minutes = String(
      d.getMinutes()
    ).padStart(2, "0");

    const ampm = hours >= 12 ? "PM" : "AM";

    const h12 = hours % 12 || 12;

    time = `${h12}:${minutes} ${ampm}`;

  }



  return `${eth} (${gc}) - ${time}`;
};

/**
 * Get list of Ethiopian years for the year selector
 * Returns last 10 years up to current Ethiopian year
 */
export function getEthiopianYearOptions() {
  const current = gregorianToEth(new Date());
  const years = [];
  for (let y = current.year; y >= current.year - 10; y--) {
    years.push({ value: y, label: `${y} E.C.` });
  }
  return years;
}

/**
 * Get Ethiopian month options
 */
export const ETHIOPIAN_MONTH_OPTIONS = [
  { value: 1,  label: "Meskerem"  },
  { value: 2,  label: "Tikmt"     },
  { value: 3,  label: "Hidar"     },
  { value: 4,  label: "Tahsas"    },
  { value: 5,  label: "Tir"       },
  { value: 6,  label: "Yekatit"   },
  { value: 7,  label: "Megabit"   },
  { value: 8,  label: "Miazia"    },
  { value: 9,  label: "Ginbot"    },
  { value: 10, label: "Sene"      },
  { value: 11, label: "Hamle"     },
  { value: 12, label: "Nehase"    },
  { value: 13, label: "Pagume"    },
];

/**
 * Get Ethiopian week options for a given year
 * Returns list of weeks with start/end dates
 * Week 1 starts on Meskerem 1
 */
export function getEthiopianWeekOptions(ethYear) {
  const yearStart = ethToGregorian(ethYear, 1, 1);
  const weeks = [];
  let current = new Date(yearStart);

  // Get current Ethiopian date for "this week" detection
  const todayEth = gregorianToEth(new Date());

  let weekNum = 1;
  while (true) {
    const weekStart = new Date(current);
    const weekEnd   = new Date(current);
    weekEnd.setDate(weekEnd.getDate() + 6);

    // Stop if week start is more than 1 year ahead
    const weekStartEth = gregorianToEth(weekStart);
    if (weekStartEth.year > ethYear) break;

    // Don't show future weeks
    if (weekStart > new Date()) break;

    const startLabel = formatEthiopianDate(weekStart, { shortMonth: true });
    const endLabel   = formatEthiopianDate(weekEnd,   { shortMonth: true });

    weeks.push({
      value:     weekNum,
      label:     `Week ${weekNum} — ${startLabel} to ${endLabel}`,
      startDate: new Date(weekStart),
      endDate:   new Date(weekEnd),
    });

    current.setDate(current.getDate() + 7);
    weekNum++;
  }

  return weeks.reverse(); // most recent first
}

/**
 * Get Gregorian date range for a given Ethiopian billing month
 * (21st to 20th of next month)
 */
export function getEthBillingMonthRange(ethYear, ethMonth) {
  const { startDate, endDate, label } =
    getEthiopianBillingMonth(ethToGregorian(ethYear, ethMonth, 21));
  return { startDate, endDate, label };
}

/**
 * Get Gregorian date range for a full Ethiopian year
 */
export function getEthYearRange(ethYear) {
  const startDate = ethToGregorian(ethYear, 1, 1);
  const endDate   = ethToGregorian(ethYear + 1, 1, 1);
  endDate.setDate(endDate.getDate() - 1);
  return { startDate, endDate };
}

/**
 * Get Ethiopian day options — returns last 60 days
 */
export function getEthiopianDayOptions() {
  const days = [];
  const today = new Date();
  for (let i = 0; i < 60; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    const eth = gregorianToEth(d);
    days.push({
      value:     d.toISOString().split("T")[0],
      label:     i === 0
        ? `Today — ${formatEthiopianDate(d)}`
        : i === 1
        ? `Yesterday — ${formatEthiopianDate(d)}`
        : formatEthiopianDate(d),
      date:      d,
    });
  }
  return days;
}


const gc = ethToGregorian(2018, 9, 14); // Ginbot 14
console.log("Gregorian:", gc);

const ec = gregorianToEth(gc);
console.log("Ethiopian:", ec);